import type { CustomerDirectoryPublishGateCode } from 'pi-kiosk-shared';
import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';
import type { AdminEntitlementSnapshot } from '../../features/entitlements/types/adminEntitlementSnapshot.js';
import type {
  AdminCommercialPlanId,
  AdminUser,
} from '../../features/auth/types/index.js';

export const authAdminMethods = {
  /**
   * Get current user with capabilities and tenant scope
   */
  async getCurrentUser(
    this: ApiClientCore,
    skipTenantPrefix = false,
    requestOptions?: RequestInit & { readonly onStart?: () => void }
  ): Promise<ApiResponse<{
    user: AdminUser;
    capabilities: readonly string[];
    tenantScope: number[] | '*';
    /** Present when user.tenantId is set; tenant code for API path prefix alignment. */
    homeTenantCode?: string | null;
    /** Additive G4 — codes for home + grants when tenantScope is a numeric list. */
    tenantScopeCodes?: readonly string[];
    entitlementSnapshot?: AdminEntitlementSnapshot | null;
    /**
     * Additive opaque access revision for apiDenyCache fingerprint invalidation.
     * Optional until BE emits it. SHADOW: not commercial deny / upgrade authority.
     */
    accessRevision?: string | null;
    /** Additive G1/G2 — global compliance scope (mirrors BE hasCapability null-tenant). */
    canDevComplianceScopeAll?: boolean;
    /**
     * Tenant id for this /me entitlement snapshot (path-explored). Null on /dev.
     * SUPER_DEV wildcard sessions use this to bind currentTenantId for audit-logs nav.
     */
    entitlementTenantId?: number | null;
    effectiveTenantRole?: string;
    /**
     * Plan Visibility — optional until BE emits on /admin/me.
     * `null` = unassigned; omit = not yet available (never coerce to Free).
     */
    commercialPlan?: AdminCommercialPlanId | null;
    /** Plan Visibility — optional Enterprise contract flag. */
    enterpriseContractActive?: boolean;
  }>> {
    return this.get('/api/v1/admin/me', skipTenantPrefix, { kind: 'default' }, requestOptions);
  },

  /**
   * Exchange raw invite token from email link for a short-lived onboarding JWT (no stored admin session).
   */
  async exchangeSuperAdminInviteSession(
    this: ApiClientCore,
    token: string,
    turnstileToken?: string
  ): Promise<ApiResponse<{ onboardingToken: string; expiresIn: string }>> {
    return this.post(
      '/api/admin/invite/session',
      {
        token,
        ...(turnstileToken !== undefined && turnstileToken.length > 0
          ? { turnstileToken }
          : {}),
      },
      false,
      { kind: 'none' }
    );
  },

  async completeSuperAdminInvite(
    this: ApiClientCore,
    body:
      | {
          onboardingJwt: string;
          username: string;
          password: string;
          turnstileToken?: string;
        }
      | {
          token: string;
          username: string;
          password: string;
          turnstileToken?: string;
        }
  ): Promise<ApiResponse<{ email: string; tenantCode: string }>> {
    if ('onboardingJwt' in body) {
      const { onboardingJwt, turnstileToken, ...rest } = body;
      return this.post(
        '/api/admin/invite/complete',
        {
          ...rest,
          ...(turnstileToken !== undefined && turnstileToken.length > 0
            ? { turnstileToken }
            : {}),
        },
        false,
        {
          kind: 'bearer',
          token: onboardingJwt,
        }
      );
    }
    return this.post('/api/admin/invite/complete', body, false);
  },

  async updateAdminUsername(this: ApiClientCore, body: { username: string }): Promise<
    ApiResponse<{ user: { id: number; email: string; username: string | null; name: string | null; role: string } }>
  > {
    return this.put('/api/v1/admin/account/username', body, false);
  },

  async updateAdminPreferredLocale(
    this: ApiClientCore,
    body: { preferredLocale: 'cs' | 'en' | 'sk' }
  ): Promise<ApiResponse<{ preferredLocale: 'cs' | 'en' | 'sk' }>> {
    return this.put('/api/v1/admin/account/preferred-locale', body, false);
  },

  async updateAdminPassword(
    this: ApiClientCore,
    body: { currentPassword?: string; newPassword: string }
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put('/api/v1/admin/account/password', body, false);
  },

  async getAdminProviderCredentialPolicies(this: ApiClientCore): Promise<ApiResponse<{
    stripeIntegrationMode?: 'DIRECT' | 'CONNECT';
    policies: Array<{
      providerKind: string;
      providerId: string;
      requiredSecretKeys: string[];
      optionalSecretKeys?: string[];
      bankAccountMode: 'forbidden' | 'optional' | 'required';
      ui?: {
        label?: string;
        helpText?: string;
        secretFieldLabels?: Record<string, string>;
      };
    }>;
  }>> {
    return this.get('/api/v1/admin/account/provider-credential-policies', false);
  },

  async getAdminPaymentPreferences(this: ApiClientCore): Promise<
    ApiResponse<{
      preferences: {
        stripeIntegrationMode: 'DIRECT' | 'CONNECT';
        stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
        customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
        /** Tenant Feature-policy purpose axis; omit until backend deploys (clients default BOTH). */
        allowedPurposes?: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
        /** Tenant currency allow-list (cascade root). */
        allowedCurrencies?: string[];
        /** Tenant default currency ∈ allow-list. */
        defaultCurrency?: string;
        /** Tenant daily cash limit (minor units); null = unlimited. */
        dailyCashLimitMinorPerTenant?: number | null;
        pickup?: {
          allowPreparingWhileUnpaid: boolean;
          pickupReadySmsEnabled: boolean;
          pickupSecondaryStaffEmail?: string | null;
          exposePickupCodeOnlyWhenPaid: boolean;
          scheduledPickupEnabled: boolean;
          scheduledPickupMinAdvanceMinutes: number;
          scheduledPickupMaxAdvanceDays: number;
          pickupWindowDurationMs: number;
          ordinaryRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
          preparedRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
          scheduledPickupSlotGranularityMinutes: number;
          autoMarkReadyAtSlotStart: boolean;
          readyLeadMinutes: number;
          maxConcurrentScheduledPickups: number;
          defaultDisplayTimeZone: string;
          lowStockHoldEnabled?: boolean;
          lowStockHoldThreshold?: number;
          lowStockHoldTtlMs?: number;
          restockEmailChannelEnabled?: boolean;
          opsMode?: 'NORMAL' | 'DELAY' | 'PAUSE';
          opsBufferMs?: number | null;
          opsDurationMs?: number | null;
          opsEndsAt?: string | null;
          opsEndOfDayRestore?: boolean;
          /** Self-Scan selective check (FR-16) — default OFF. */
          tenantSelfScanSelectiveCheckEnabled?: boolean;
          /** Sample rate % when selective check is ON (1–100). */
          samplePercent?: number;
        };
      };
    }>
  > {
    return this.get('/api/v1/admin/account/payment-preferences', false);
  },

  async getAdminProductPaymentCatalogReadiness(this: ApiClientCore): Promise<
    ApiResponse<{
      ready: boolean;
      flags: {
        hasProduct: boolean;
        hasSalesPoint: boolean;
        hasStockedProduct: boolean;
        hasEnabledBankMethod: boolean;
        hasReadyPaymentMethod: boolean;
      };
      failedGates: readonly (
        | 'PRODUCT'
        | 'SALES_POINT'
        | 'STOCK'
        | 'BANK_METHOD'
        | 'METHOD_READINESS'
      )[];
      directoryEligibility: {
        listable: boolean;
        listedAt: string | null;
        hasPublishablePay: boolean;
        failedPublishGates: readonly CustomerDirectoryPublishGateCode[];
        stickyOos: boolean;
        delistRisk: boolean;
        /** G5 — cash Approach A remediation jump (not banks Ověřit). */
        cashApproachARemediation: boolean;
        salesPoints: readonly {
          salesPointId: number;
          listable: boolean;
          listedAt: string | null;
          hasPublishablePay: boolean;
          failedPublishGates: readonly string[];
          stickyOos: boolean;
          delistRisk: boolean;
        }[];
      };
    }>
  > {
    return this.get('/api/v1/admin/account/product-payment-catalog-readiness', false);
  },

  async getAdminConfigurationProgressDismiss(
    this: ApiClientCore
  ): Promise<ApiResponse<{ dismissed: boolean }>> {
    return this.get('/api/v1/admin/account/configuration-progress-dismiss', false);
  },

  async setAdminConfigurationProgressDismiss(
    this: ApiClientCore,
    body: { dismissed: boolean }
  ): Promise<ApiResponse<{ dismissed: boolean }>> {
    return this.put('/api/v1/admin/account/configuration-progress-dismiss', body, false);
  },

  /**
   * @deprecated Tenant PUT returns 403 PROVIDER_DEFAULTS_DEV_ONLY (Wave 3 Option C).
   * Platform staff: use {@link updateDevTenantPaymentPreferences}.
   */
  async updateAdminPaymentPreferences(
    this: ApiClientCore,
    body: {
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      /** Tenant currency allow-list (cascade root). */
      allowedCurrencies?: string[];
      /** Tenant default currency ∈ allow-list. */
      defaultCurrency?: string;
      /** Tenant daily cash limit (minor units); null = unlimited. */
      dailyCashLimitMinorPerTenant?: number | null;
      pickup?: {
        allowPreparingWhileUnpaid?: boolean;
        pickupReadySmsEnabled?: boolean;
        pickupSecondaryStaffEmail?: string | null;
        exposePickupCodeOnlyWhenPaid?: boolean;
        scheduledPickupEnabled?: boolean;
        scheduledPickupMinAdvanceMinutes?: number;
        scheduledPickupMaxAdvanceDays?: number;
        pickupWindowDurationMs?: number;
        ordinaryRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
        preparedRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
        scheduledPickupSlotGranularityMinutes?: number;
        autoMarkReadyAtSlotStart?: boolean;
        readyLeadMinutes?: number;
        maxConcurrentScheduledPickups?: number;
        defaultDisplayTimeZone?: string;
        lowStockHoldEnabled?: boolean;
        lowStockHoldThreshold?: number;
        lowStockHoldTtlMs?: number;
        restockEmailChannelEnabled?: boolean;
        opsMode?: 'NORMAL' | 'DELAY' | 'PAUSE';
        opsBufferMs?: number | null;
        opsDurationMs?: number | null;
        opsEndsAt?: string | null;
        opsEndOfDayRestore?: boolean;
        restoreOpsToNormal?: boolean;
        /** Self-Scan selective check (FR-16) — default OFF. */
        tenantSelfScanSelectiveCheckEnabled?: boolean;
        /** Sample rate % when selective check is ON (1–100). */
        samplePercent?: number;
      };
    }
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put('/api/v1/admin/account/payment-preferences', body, false);
  },

  async rebindAdminTenantSession(
    this: ApiClientCore,
    body: { tenantId: number; tenantCode: string },
  ): Promise<ApiResponse<{ rebound: boolean }>> {
    return this.post('/api/v1/admin/session/rebind-tenant', body, false);
  },

  async rebindDevTenantSession(
    this: ApiClientCore,
    body: { tenantId: number; tenantCode: string },
  ): Promise<ApiResponse<{ rebound: boolean }>> {
    return this.post('/api/v1/dev/session/rebind-tenant', body, true);
  },

  async createDelegatedTenantAdmin(
    this: ApiClientCore,
    body: {
      email: string;
      role: 'ADMIN';
      provisionMode: 'password' | 'oidc_invite';
      password?: string;
      name?: string;
      username?: string;
      /** Invite email language (cs|en|sk); Accept-Language used when omitted. */
      locale?: 'cs' | 'en' | 'sk';
      templateId?: string;
      salesPointIds?: number[];
    }
  ): Promise<ApiResponse<{ user: { id: number; email: string; username: string | null; name: string | null; role: string; status: string; tenantId: number | null } }>> {
    return this.post('/api/v1/admin/tenant-admins', body, false);
  },

  /**
   * Public self-serve tenant registration — no auth cookie / Bearer.
   * Paths: POST /api/v1/public/tenant-register/{start|verify|complete}
   */
  async startTenantSelfServeRegistration(
    this: ApiClientCore,
    body: {
      email?: string;
      phone?: string;
      /** cs | en | sk — OTP email/SMS language. */
      locale?: 'cs' | 'en' | 'sk';
      turnstileToken?: string;
    }
  ): Promise<
    ApiResponse<{
      jti: string;
      channel: 'EMAIL_OTP' | 'PHONE_OTP';
      expiresAt: string;
      maskedIdentifier: string;
    }>
  > {
    const { turnstileToken, ...rest } = body;
    return this.post(
      '/api/v1/public/tenant-register/start',
      {
        ...rest,
        ...(turnstileToken !== undefined && turnstileToken.length > 0
          ? { turnstileToken }
          : {}),
      },
      true,
      { kind: 'none' }
    );
  },

  async verifyTenantSelfServeRegistration(
    this: ApiClientCore,
    body: {
      jti: string;
      code: string;
      turnstileToken?: string;
    }
  ): Promise<ApiResponse<{ registrationToken: string; expiresIn: number | string }>> {
    const { turnstileToken, ...rest } = body;
    return this.post(
      '/api/v1/public/tenant-register/verify',
      {
        ...rest,
        ...(turnstileToken !== undefined && turnstileToken.length > 0
          ? { turnstileToken }
          : {}),
      },
      true,
      { kind: 'none' }
    );
  },

  async completeTenantSelfServeRegistration(
    this: ApiClientCore,
    body: {
      registrationToken: string;
      tenantName: string;
      tenantCode: string;
      username: string;
      password: string;
      passwordConfirm: string;
      turnstileToken?: string;
    }
  ): Promise<
    ApiResponse<{
      tenantCode: string;
      tenantId: number;
      email?: string;
      phone?: string;
    }>
  > {
    const { turnstileToken, ...rest } = body;
    return this.post(
      '/api/v1/public/tenant-register/complete',
      {
        ...rest,
        ...(turnstileToken !== undefined && turnstileToken.length > 0
          ? { turnstileToken }
          : {}),
      },
      true,
      { kind: 'none' }
    );
  },
};
